import { startTransition, useEffect, useMemo, useRef, useState } from 'react'
import i18n from 'i18next'
import { useGetUserSession } from '@/shared/hooks/api/auth'
import {
  isEuLexRegulatoryNewsSubscription,
  useDismissOrganizationAlert,
  useMarkOrganizationAlertRead,
  useOrganizationAlertsInfinite,
  useRegulatoryNewsSubscriptions,
} from '@/shared/hooks/api/regulatoryNews'

const REGULATORY_ALERTS_NAMESPACES = ['regulatory-alerts'] as const

export function useNewsroomPageState() {
  const { data: session } = useGetUserSession()
  const organizationId = session?.organizationId

  const [settingsOpen, setSettingsOpen] = useState(false)
  const [includeDismissed, setIncludeDismissed] = useState(false)
  const [selectedAlertId, setSelectedAlertId] = useState<string | null>(null)
  const [dismissTargetId, setDismissTargetId] = useState<string | null>(null)

  const { data: subscriptions = [] } =
    useRegulatoryNewsSubscriptions(organizationId)

  const hasEuSubscriptions = useMemo(
    () => subscriptions.some(isEuLexRegulatoryNewsSubscription),
    [subscriptions]
  )

  const alertsQuery = useOrganizationAlertsInfinite({
    organizationId,
    includeDismissed,
  })

  const markRead = useMarkOrganizationAlertRead()
  const dismissAlert = useDismissOrganizationAlert()
  const autoReadAlertIdRef = useRef<string | null>(null)

  const alerts = useMemo(
    () => alertsQuery.data?.pages.flatMap((page) => page.items) ?? [],
    [alertsQuery.data]
  )

  const selectedAlert = useMemo(
    () => alerts.find((alert) => alert.id === selectedAlertId) ?? null,
    [alerts, selectedAlertId]
  )

  useEffect(() => {
    void i18n.loadNamespaces([...REGULATORY_ALERTS_NAMESPACES])
  }, [])

  useEffect(() => {
    if (!selectedAlert || !organizationId) {
      return
    }
    if (selectedAlert.readAt !== null) {
      return
    }
    if (autoReadAlertIdRef.current === selectedAlert.id) {
      return
    }
    autoReadAlertIdRef.current = selectedAlert.id

    void markRead.mutateAsync({
      organizationId,
      alertId: selectedAlert.id,
    })
  }, [selectedAlert, organizationId, markRead])

  const handleSelectAlert = (alertId: string) => {
    startTransition(() => {
      setSelectedAlertId(alertId)
    })
  }

  const handleRequestDismiss = (alertId: string) => {
    setDismissTargetId(alertId)
  }

  const handleDismissDialogOpenChange = (open: boolean) => {
    if (!open) {
      setDismissTargetId(null)
    }
  }

  const handleConfirmDismiss = async () => {
    if (!organizationId || !dismissTargetId) {
      return
    }

    await dismissAlert.mutateAsync({
      organizationId,
      alertId: dismissTargetId,
    })
    setDismissTargetId(null)
    if (selectedAlertId === dismissTargetId) {
      setSelectedAlertId(null)
    }
  }

  const handleMarkRead = (alertId: string) => {
    if (!organizationId) {
      return
    }
    void markRead.mutateAsync({ organizationId, alertId })
  }

  const isLoading = alertsQuery.isLoading
  const showEmptyNoSubscriptions = !isLoading && !hasEuSubscriptions
  const showEmptyNoAlerts =
    !isLoading && hasEuSubscriptions && alerts.length === 0
  const showAlertsPanel =
    !showEmptyNoSubscriptions && !showEmptyNoAlerts && alerts.length > 0

  return {
    organizationId,
    settingsOpen,
    setSettingsOpen,
    includeDismissed,
    setIncludeDismissed,
    selectedAlertId,
    dismissTargetId,
    alerts,
    selectedAlert,
    alertsQuery,
    markRead,
    handleSelectAlert,
    handleRequestDismiss,
    handleDismissDialogOpenChange,
    handleConfirmDismiss,
    handleMarkRead,
    isLoading,
    showEmptyNoSubscriptions,
    showEmptyNoAlerts,
    showAlertsPanel,
  }
}
